import { trackEvent } from '../../services/analytics';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, CircularProgress, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, IconButton, Stack, TextField, Typography } from '@mui/material';
import { ArrowBack, Restaurant, Send } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { COMMUNITY_CONDUCT_URL, COMMUNITY_CONDUCT_VERSION } from '../../constants/communityConduct';
import { trackChatPresence } from '../../services/chatPresence';
import { useAuth } from '../../context/AuthContext';
import { acceptCommunityConduct, subscribeToCommunityConduct, canUseTableChat, MAX_MESSAGE_LENGTH, newMessageId, sendTableMessage, subscribeToChatTable, subscribeToTableMessages } from '../../services/tableChat';

export default function TableChatPage() {
  const { group_id: groupId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [tableState, setTableState] = useState(null);
  const [messageState, setMessageState] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [retry, setRetry] = useState(0);
  const [consentState, setConsentState] = useState(null);
  const [consentError, setConsentError] = useState('');
  const [conductChecked, setConductChecked] = useState(false);
  const [acceptingConduct, setAcceptingConduct] = useState(false);
  const bottom = useRef(null);
  const attemptedMessage = useRef(null);
  const inFlight = useRef(false);
  const userId = currentUser?.uid;
  const sessionKey = `${groupId}:${userId}`;
  const group = tableState?.key === sessionKey ? tableState.group : null;
  const allowed = canUseTableChat(group, userId);
  const conductAccepted = consentState?.userId === userId
    && consentState.consent?.version === COMMUNITY_CONDUCT_VERSION && Boolean(consentState.consent?.acceptedAt);
  const canReadMessages = allowed && conductAccepted;
  const messages = canReadMessages && messageState?.key === sessionKey ? messageState.messages : [];
  const messagesReady = messageState?.key === sessionKey;
  const newestMessageId = messages[messages.length - 1]?.id;

  useEffect(() => {
    setLoadError('');
    setDraft('');
    setSendError('');
    attemptedMessage.current = null;
    if (!userId || currentUser.isAnonymous) return undefined;
    return subscribeToChatTable(groupId, (table) => setTableState({ key: sessionKey, group: table }),
      () => setLoadError('Non è stato possibile aprire la chat. Riprova.'));
  }, [groupId, userId, currentUser?.isAnonymous, sessionKey, retry]);

  useEffect(() => {
    setConsentError('');
    setConductChecked(false);
    if (!userId || currentUser?.isAnonymous) return undefined;
    return subscribeToCommunityConduct(userId,
      (consent) => setConsentState({ userId, consent }),
      () => {
        setConsentState(null);
        setConsentError('Non è stato possibile verificare l’accettazione. Riprova.');
      });
  }, [userId, currentUser?.isAnonymous, retry]);

  async function handleAcceptConduct() {
    if (!conductChecked || acceptingConduct) return;
    setAcceptingConduct(true);
    setConsentError('');
    try {
      await acceptCommunityConduct();
    } catch {
      setConsentError('Accettazione non salvata. Riprova per accedere alla chat.');
    } finally {
      setAcceptingConduct(false);
    }
  }

  useEffect(() => {
    if (!canReadMessages) return undefined;
    return subscribeToTableMessages(groupId, (items) => setMessageState({ key: sessionKey, messages: items }),
      () => {
        setMessageState(null);
        setLoadError('Non è stato possibile caricare i messaggi. Riprova.');
      });
  }, [canReadMessages, groupId, sessionKey, retry]);

  useEffect(() => {
    if (!canReadMessages || !messagesReady || loadError) return undefined;
    return trackChatPresence(groupId, userId);
  }, [canReadMessages, messagesReady, loadError, groupId, userId]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [newestMessageId]);

  async function handleSend(event) {
    event.preventDefault();
    if (inFlight.current || !canReadMessages || !draft.trim()) return;
    inFlight.current = true;
    setSending(true);
    setSendError('');
    const text = draft.trim();
    if (attemptedMessage.current?.text !== text) {
      attemptedMessage.current = { id: newMessageId(groupId), text };
    }
    try {
      await sendTableMessage(groupId, { ...attemptedMessage.current, senderName: userProfile?.displayName });
      trackEvent('chat_message_sent');
      setDraft('');
      attemptedMessage.current = null;
    } catch {
      setSendError('Messaggio non inviato. Il testo è conservato: riprova.');
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  const backToTable = () => navigate(`/table/${groupId}`);
  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', maxWidth: 720, mx: 'auto', pt: 'var(--cv-safe-area-top)' }}>
      <Stack component="header" direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1.5, borderBottom: '1px solid #eadbd9' }}>
        <IconButton aria-label="Torna al tavolo" onClick={backToTable} sx={{ color: 'primary.main', width: 44, height: 44 }}><ArrowBack /></IconButton>
        <Avatar variant="rounded" sx={{ bgcolor: '#f0dfd6', color: 'primary.main', borderRadius: '13px' }}><Restaurant /></Avatar>
        <Box sx={{ minWidth: 0 }}><Typography component="h1" sx={{ fontWeight: 800, fontSize: 14 }}>{group?.name || 'Chat del tavolo'}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{allowed ? `${group.attendeeIds.length} partecipanti` : 'Riservata ai commensali'}</Typography></Box>
      </Stack>
      {allowed && <Box component="button" onClick={backToTable} sx={{ textAlign: 'left', border: 0, borderBottom: '1px solid #eadbd9', bgcolor: '#f2eae5', px: 2.5, py: 1.5, color: 'text.primary', cursor: 'pointer' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{group.startAt ? dayjs(group.startAt.toDate()).format('DD/MM · HH:mm') : 'Il tuo tavolo'} · {group.location?.place || 'Luogo da confermare'}</Typography>
        <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25 }}>Torna ai dettagli del tavolo →</Typography>
      </Box>}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        {loadError ? <Alert severity="error" action={<IconButton aria-label="Riprova" onClick={() => { setTableState(null); setMessageState(null); setRetry((value) => value + 1); }}>↻</IconButton>}>{loadError}</Alert>
          : !userId || currentUser?.isAnonymous ? <Alert severity="info">Accedi con il tuo account per aprire la chat del tavolo.</Alert>
            : tableState?.key !== sessionKey ? <CircularProgress aria-label="Caricamento chat" />
              : !allowed ? <Alert severity="info">La chat è disponibile ai commensali quando c’è almeno un partecipante oltre all’organizzatore.</Alert>
                : !conductAccepted ? <>
                  {consentError ? <Alert severity="error" action={<Button onClick={() => setRetry((value) => value + 1)}>Riprova</Button>}>{consentError}</Alert>
                    : consentState?.userId !== userId && <CircularProgress aria-label="Verifica codice di condotta" />}
                </> : <>
                  <Typography sx={{ textAlign: 'center', fontSize: 11, color: '#8b777a', lineHeight: '18px', mx: 2, mb: 3 }}>Questa chat è riservata ai partecipanti del tavolo.<br />Un saluto è un ottimo modo per iniziare.</Typography>
                  {!messagesReady ? <CircularProgress aria-label="Caricamento messaggi" /> : messages.length === 0 && <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>Nessun messaggio. Saluta il tavolo!</Typography>}
                  <Box role="log" aria-label="Messaggi del tavolo" aria-live="polite" aria-relevant="additions">
                    {messages.map((message, index) => {
                      const own = message.senderId === userId;
                      const date = message.createdAt?.toDate();
                      const previousDate = messages[index - 1]?.createdAt?.toDate();
                      const showDate = date && (!previousDate || !dayjs(date).isSame(previousDate, 'day'));
                      return <React.Fragment key={message.id}>
                        {showDate && <Typography sx={{ textAlign: 'center', fontSize: 10, color: '#8b777a', mb: 2 }}>{dayjs(date).format('DD/MM/YYYY')}</Typography>}
                        <Stack direction="row" alignItems="flex-end" spacing={0.75} sx={{ justifyContent: own ? 'flex-end' : 'flex-start', mb: 1.75 }}>
                          {!own && <Avatar sx={{ width: 18, height: 18, fontSize: 9, flexShrink: 0, bgcolor: '#e3e8d9', color: '#5b6d47' }}>{message.senderName?.charAt(0)}</Avatar>}
                          <Box sx={{ maxWidth: own ? '82%' : 'calc(100% - 24px)', minWidth: 0, px: 1.5, py: 1.25, border: '1px solid', borderColor: own ? 'primary.main' : '#eee3de', bgcolor: own ? 'primary.main' : '#fff', color: own ? '#fff' : 'text.primary', borderRadius: own ? '15px 15px 4px 15px' : '15px 15px 15px 4px' }}>
                            {!own && <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'primary.main', mb: 0.5 }}>{message.senderName}{message.senderId === group.createdBy && <Box component="span" sx={{ color: '#8b777a', fontWeight: 400, ml: 1 }}>Organizzatore</Box>}</Typography>}
                            <Typography sx={{ fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.text}</Typography>
                            <Typography sx={{ textAlign: 'right', fontSize: 9, color: own ? '#e8b9c8' : '#8b777a', mt: 0.5 }}>{message.pending ? 'Invio in corso…' : `${date ? dayjs(date).format('HH:mm') : ''}${own ? ' · Inviato' : ''}`}</Typography>
                          </Box>
                        </Stack>
                      </React.Fragment>;
                    })}
                  </Box><div ref={bottom} />
                </>}
      </Box>
      {canReadMessages && !loadError && <Box component="form" onSubmit={handleSend} sx={{ borderTop: '1px solid #eadbd9', p: 1.5, pb: 'calc(12px + var(--cv-safe-area-bottom))' }}>
        {sendError && <Alert severity="error" sx={{ mb: 1 }}>{sendError}</Alert>}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField fullWidth multiline maxRows={4} size="small" placeholder="Scrivi al tavolo…" value={draft} disabled={sending} onChange={(event) => setDraft(event.target.value)} inputProps={{ maxLength: MAX_MESSAGE_LENGTH, 'aria-label': 'Messaggio al tavolo' }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '24px', bgcolor: '#fff', fontSize: 13, py: 1.5 } }} />
          <IconButton type="submit" aria-label="Invia messaggio" disabled={!draft.trim() || sending || !messagesReady} sx={{ bgcolor: 'primary.main', color: '#fff', width: 44, height: 44, '&:hover': { bgcolor: 'primary.dark' } }}><Send sx={{ fontSize: 21 }} /></IconButton>
        </Stack>
      </Box>}
      <Dialog open={Boolean(allowed && !conductAccepted && consentState?.userId === userId)} disableEscapeKeyDown
        aria-labelledby="community-conduct-title" fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: 3, p: 1 } } }}>
        <DialogTitle id="community-conduct-title" sx={{ color: 'primary.main', fontWeight: 800, fontSize: 22 }}>
          Codice di condotta della community Convivial
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>
            Prima di accedere alle chat, leggi e accetta il codice di condotta della community Convivial.
          </Typography>
          <Link href={COMMUNITY_CONDUCT_URL} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-block', mb: 2, fontWeight: 700 }}>
            Leggi il codice di condotta ↗
          </Link>
          <FormControlLabel control={<Checkbox checked={conductChecked} disabled={acceptingConduct}
            onChange={(event) => setConductChecked(event.target.checked)} />}
            label="Ho letto e accetto il codice di condotta della community Convivial." />
          {consentError && <Alert severity="error" sx={{ mt: 2 }}>{consentError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, flexDirection: 'column', gap: 1, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
          <Button fullWidth variant="contained" disabled={!conductChecked || acceptingConduct} onClick={handleAcceptConduct}>
            {acceptingConduct ? 'Salvataggio…' : 'Accetta e apri la chat'}
          </Button>
          <Button fullWidth disabled={acceptingConduct} onClick={backToTable}>Torna al tavolo</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
